import {FunctionComponent, lazy, Suspense, useMemo} from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import waveSource from '@/assets/images/bg-wave.png';
import {useInspector} from '@/components/document/hooks/use-inspector';
import {DevInspectorToggle} from '@/components/document/inspector/dev-inspector-toggle';
import {NewDocumentCreationDialogButton} from '@/components/new-document-creation';
import Spinner from '@/components/spinner';
import {useUserProfileContext} from '@/contexts/user-profile';
import environment from '@/environment';
import {useFormatFullName} from '@/hooks/use-format-fullname';

const DevInspector = lazy(() => import('@/components/document/inspector/dev-inspector'));

const Home: FunctionComponent = () => {
  const {t} = useTranslation();
  const formatFullName = useFormatFullName();
  const {profile} = useUserProfileContext();
  const isInspectorVisible = useInspector(state => state.isVisible);
  const devInspectorPosition = useInspector(state => state.position);

  const fullName = useMemo(() => {
    return formatFullName(profile.cons2_firstName, profile.cons1_lastName, {format: 'shortenName'});
  }, [profile.cons1_lastName, profile.cons2_firstName, formatFullName]);

  return (
    <div
      className={clsx(
        'flex max-w-full grow gap-4',
        devInspectorPosition === 'side' && 'flex-row items-start',
        devInspectorPosition === 'bottom' && 'flex-col',
      )}
    >
      <div className="w-full rounded-4 bg-white py-12">
        {environment.DEBUG && (
          <div className="flex justify-end px-6 pb-4">
            <DevInspectorToggle data-testid="home-debug-panel" />
          </div>
        )}

        <div className="flex flex-col items-center">
          <div className="mb-8 text-lg font-semibold">{t('routes.home.title', {fullName})}</div>

          <div>
            <NewDocumentCreationDialogButton data-testid="new-document-button" />
          </div>

          <img src={waveSource} alt="" className="mt-52 w-full" />
        </div>
      </div>

      {isInspectorVisible && (
        <Suspense fallback={<Spinner />}>
          <DevInspector mode="settings" />
        </Suspense>
      )}
    </div>
  );
};

export default Home;
